import { useState } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  DefaultConfigurationPageDocument,
  LegalEntityWithFinanceAccountsFragment,
  UpsertAccountingSettingDocument,
} from 'generated/resources/graphql'

import Filter from 'modules/core/components/Filter'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useForm } from 'modules/core/hooks/useForm'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app, { Flash } from 'modules/core/store/models/app'

import { DefaultConfigurationRow } from 'modules/finance/components/Accounting/DefaultConfigurationRow'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

interface FormValues {
  [key: string]: string | undefined
  defaultAccountForBanksId?: string
  defaultAccountForPurchaseInvoicesId?: string
  defaultAccountForSuspenseId?: string
  defaultAccountForVendorsId?: string
}

export interface OutletContext {
  legalEntity?: LegalEntityWithFinanceAccountsFragment
}

const DefaultConfiguration = () => {
  const [legalEntity, setLegalEntity] = useState<LegalEntityWithFinanceAccountsFragment>()

  const { company } = useCurrentSession()

  const {
    data: page,
    loading,
    error,
  } = useQuery(DefaultConfigurationPageDocument, {
    variables: {
      companyId: company.id,
      legalEntityId: legalEntity?.id || '-1',
    },
  })
  const legalEntities = useUnsafeMemo(
    () => page?.companies?.legalEntities ?? [],
    [page?.companies?.legalEntities]
  )
  const accountingSettings = page?.finance?.accountingSettings ?? []
  const canSeeLedgerAccountSettings =
    page?.finance?._policies?.settingsSeeAccountingSettingsLedgerAccounts?.can ?? false
  const seeSalesLedgerAccounts = page?.finance?._policies?.accountingSeeSalesAccounts?.can ?? false
  const seePurchaseLedgerAccounts =
    page?.finance?._policies?.accountingSeePurchaseAccounts?.can ?? false

  const [upsertAccountingSetting, { loading: upserting }] = useMutation(
    UpsertAccountingSettingDocument,
    {
      extractorFn: (data) => data.financeMutations.upsertFinanceAccountingSetting,
    }
  )

  const ledgerAccounts = legalEntity?.financeAccounts ?? []
  const ledgerAccountsBank = useUnsafeMemo(
    () => legalEntity?.financeAccounts.filter((acc) => acc.type === 'bank'),
    [legalEntity?.financeAccounts]
  )

  useUnsafeEffect(() => {
    if (legalEntity) return
    setLegalEntity(legalEntities?.find((node) => node.main))
  }, [legalEntities, legalEntity])

  const form = useForm<FormValues>({
    defaultValues: {
      defaultAccountForBanksId: accountingSettings[0]?.defaultAccountForBanks?.id,
      defaultAccountForPurchaseInvoicesId:
        accountingSettings[0]?.defaultAccountForPurchaseInvoices?.id,
      defaultAccountForSuspenseId: accountingSettings[0]?.defaultAccountForSuspense?.id,
      defaultAccountForVendorsId: accountingSettings[0]?.defaultAccountForVendors?.id,
      defaultAccountForEmployeesId: accountingSettings[0]?.defaultAccountForEmployees?.id,
      defaultAccountForExpensesId: accountingSettings[0]?.defaultAccountForExpenses?.id,
      defaultAccountForSaleInvoicesId: accountingSettings[0]?.defaultAccountForSaleInvoices?.id,
      defaultAccountForClientsId: accountingSettings[0]?.defaultAccountForClients?.id,
    },
    enableReinitialize: true,
    onSubmit: async (data) => {
      if (!legalEntity) return { success: true, data: data }

      const result = await upsertAccountingSetting({
        variables: {
          ...data,
          companyId: company.id,
          legalEntityId: legalEntity.id,
        },
      }).then((res) => {
        track(trackingEvents.accounting.settings.defaultConfiguration.save)
        return res
      })
      const handled = handleResult(result, () => undefined)

      const flashMessage: Flash = handled.success
        ? {
            type: 'correct',
            message: i18n.t('finance.settings.accounting.default_configuration.success'),
            time: 2000,
          }
        : { type: 'wrong', message: i18n.t('common.error'), time: 2000 }

      app.setFlash(flashMessage)
      return handled
    },
  })

  const configurationRows = [
    {
      label: i18n.t('finance.settings.accounting.default_configuration.ledger_account.bank'),
      description: i18n.t(
        'finance.settings.accounting.default_configuration.ledger_account.bank_description'
      ),
      valueKey: 'defaultAccountForBanksId',
      filterBanks: true,
    },
    ...(seePurchaseLedgerAccounts
      ? [
          {
            label: i18n.t(
              'finance.settings.accounting.default_configuration.ledger_account.purchase_invoice'
            ),
            description: i18n.t(
              'finance.settings.accounting.default_configuration.ledger_account.purchase_invoice_description'
            ),
            valueKey: 'defaultAccountForPurchaseInvoicesId',
            filterBanks: false,
          },
        ]
      : []),
    ...(seeSalesLedgerAccounts
      ? [
          {
            label: i18n.t(
              'finance.settings.accounting.default_configuration.ledger_account.sale_invoice'
            ),
            description: i18n.t(
              'finance.settings.accounting.default_configuration.ledger_account.sale_invoice_description'
            ),
            valueKey: 'defaultAccountForSaleInvoicesId',
            filterBanks: false,
          },
        ]
      : []),
    {
      label: i18n.t('finance.settings.accounting.default_configuration.ledger_account.suspense'),
      description: i18n.t(
        'finance.settings.accounting.default_configuration.ledger_account.suspense_description'
      ),
      valueKey: 'defaultAccountForSuspenseId',
      filterBanks: false,
    },
    ...(seePurchaseLedgerAccounts
      ? [
          {
            label: i18n.t(
              'finance.settings.accounting.default_configuration.ledger_account.vendor'
            ),
            description: i18n.t(
              'finance.settings.accounting.default_configuration.ledger_account.vendor_description'
            ),
            valueKey: 'defaultAccountForVendorsId',
            filterBanks: false,
          },
        ]
      : []),
    ...(seeSalesLedgerAccounts
      ? [
          {
            label: i18n.t(
              'finance.settings.accounting.default_configuration.ledger_account.client'
            ),
            description: i18n.t(
              'finance.settings.accounting.default_configuration.ledger_account.client_description'
            ),
            valueKey: 'defaultAccountForClientsId',
            filterBanks: false,
          },
        ]
      : []),
    ...(seePurchaseLedgerAccounts
      ? [
          {
            label: i18n.t(
              'finance.settings.accounting.default_configuration.ledger_account.expense'
            ),
            description: i18n.t(
              'finance.settings.accounting.default_configuration.ledger_account.expense_description'
            ),
            valueKey: 'defaultAccountForExpensesId',
            filterBanks: false,
          },
          {
            label: i18n.t(
              'finance.settings.accounting.default_configuration.ledger_account.employee'
            ),
            description: i18n.t(
              'finance.settings.accounting.default_configuration.ledger_account.employee_description'
            ),
            valueKey: 'defaultAccountForEmployeesId',
            filterBanks: false,
          },
        ]
      : []),
  ]

  const handleSelect = (id: string, valueKey: string) => {
    form.setValues({ ...form.values, [valueKey]: id })
  }

  return (
    <ResponsivePage.Body authorized={canSeeLedgerAccountSettings} loading={loading} error={error}>
      <Box height="full">
        <Box width="fit-content" paddingTop="s32" paddingBottom="s32">
          <Filter.SingleChoice
            label={i18n.t('finance.workspace.legal_entity_scope.selector_label')}
            options={legalEntities.map((le) => ({ label: le.legalName ?? '', value: le }))}
            value={legalEntity}
            onValueChange={setLegalEntity}
            noFooter
          />
        </Box>
        {legalEntity && (
          <Box gap="s32">
            <Text size={'400'} weight="semibold">
              {i18n.t('finance.settings.accounting.default_configuration.ledger_account.title')}
            </Text>
            {configurationRows.map(({ label, description, valueKey, filterBanks }) => (
              <DefaultConfigurationRow
                key={valueKey}
                label={label}
                description={description}
                legalEntityId={legalEntity.id}
                selectedLedgerAccountId={form.values[valueKey]}
                accounts={filterBanks ? ledgerAccountsBank : ledgerAccounts}
                disabled={loading}
                onSelect={(id) => handleSelect(id, valueKey)}
              />
            ))}
            <Box alignItems="flexEnd">
              <Button
                disabled={!form.dirty || loading || upserting}
                onClick={() => form.handleSubmit()}
              >
                {i18n.t('common.save')}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </ResponsivePage.Body>
  )
}

export default DefaultConfiguration
